import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiMaximize2, FiImage } from 'react-icons/fi';
import { useLightbox } from '../../context/LightboxContext';
import { resolveDynamicCategoryImage } from '../../services/categoryService';
import './CategoryCard.css';

const CategoryCard = ({ category, subcategoryCount = 0, onSelectParent = null }) => {
    const { openLightbox } = useLightbox();
    const imgRef = useRef(null);

    const imageUrl = resolveDynamicCategoryImage(category);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);

    useEffect(() => {
        setImgLoaded(false);
        setImgFailed(false);

        const el = imgRef.current;
        if (imageUrl && el && el.complete) {
            if (el.naturalWidth > 0) {
                setImgLoaded(true);
            } else {
                setImgFailed(true);
            }
        }
    }, [imageUrl]);

    const hasValidImage = Boolean(imageUrl) && !imgFailed;
    const isCoverFit = category?.image_fit === 'cover';
    const altText = category?.image_alt || category?.name || 'Category';
    const hasSubcategories = subcategoryCount > 0 && typeof onSelectParent === 'function';

    const subtitleText =
        category?.subtitle ||
        (subcategoryCount > 0
            ? `${subcategoryCount} ${subcategoryCount === 1 ? 'Subcategory' : 'Subcategories'}`
            : 'Genuine Brand Units');

    const handleZoomClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!hasValidImage) return;
        openLightbox(imageUrl, `${category.name} | Rahber Corporation`, category.name);
    };

    const cardInnerContent = (
        <>
            <div className="category-card-photo__media">
                {hasValidImage ? (
                    <>
                        {!imgLoaded && (
                            <div
                                className="category-card-photo__shimmer"
                                aria-hidden="true"
                            />
                        )}
                        <img
                            ref={imgRef}
                            src={imageUrl}
                            alt={altText}
                            className={`category-card-photo__img${
                                isCoverFit ? ' category-card-photo__img--cover' : ''
                            }${imgLoaded ? ' is-loaded' : ' is-loading'}`}
                            loading="lazy"
                            decoding="async"
                            onLoad={() => {
                                setImgLoaded(true);
                                setImgFailed(false);
                            }}
                            onError={() => {
                                setImgFailed(true);
                                setImgLoaded(false);
                            }}
                        />
                    </>
                ) : (
                    <div
                        className="category-card-photo__empty"
                        role="img"
                        aria-label={
                            imgFailed
                                ? `${category.name} image unavailable`
                                : `${category.name} category`
                        }
                    >
                        <FiImage
                            className="category-card-photo__empty-icon"
                            aria-hidden="true"
                        />
                        <span className="category-card-photo__empty-label">
                            {imgFailed ? 'Image unavailable' : 'No image uploaded'}
                        </span>
                    </div>
                )}
            </div>

            <div className="category-card-photo__body">
                <div className="category-card-photo__content">
                    <h3 className="category-card-photo__title">{category.name}</h3>
                    <span className="category-card-photo__sub">{subtitleText}</span>
                </div>

                <div className="category-card-photo__arrow" aria-hidden="true">
                    <FiChevronRight />
                </div>
            </div>
        </>
    );

    return (
        <div className="category-card-photo">
            {/* Quick Tap-to-Zoom Lightbox Button (shown only when a real category image is loaded) */}
            {hasValidImage && imgLoaded && (
                <button
                    type="button"
                    className="category-card-zoom-btn"
                    onClick={handleZoomClick}
                    aria-label={`View full ${category.name} image`}
                    title="Tap to enlarge photo"
                >
                    <FiMaximize2 />
                </button>
            )}

            {hasSubcategories ? (
                <button
                    type="button"
                    className="category-card-inner-link category-card-inner-btn"
                    onClick={() => onSelectParent(category)}
                    aria-label={`Explore ${category.name} subcategories`}
                >
                    {cardInnerContent}
                </button>
            ) : (
                <Link
                    to={`/products?category=${encodeURIComponent(category._id)}`}
                    className="category-card-inner-link"
                    aria-label={`Shop ${category.name}`}
                >
                    {cardInnerContent}
                </Link>
            )}
        </div>
    );
};

export default CategoryCard;

